import { ShieldCheck, Info } from 'lucide-react';

/**
 * P2-11 — Constitutional / Jurisdictional Deference banner.
 *
 * Surfaces the canonical NACIS Patent Appendix 4 invariants on every VERIS
 * admin surface so reviewers always see the deference framing:
 *
 *   - VERIS is advisory only; final decisions rest with the state/local
 *     election authority.
 *   - VERIS does not alter, supersede, or reinterpret constitutional
 *     authority over voter eligibility or election administration.
 *   - All automation outputs are descriptive, not directive — human
 *     confirmation is required for authoritative actions.
 *
 * Use the `compact` variant on dense list pages; the default (full) variant
 * on entry/landing pages.
 */
export default function DeferenceBanner({ compact = false, className = '' }) {
  if (compact) {
    return (
      <div
        role="note"
        aria-label="VERIS deference notice"
        className={`mb-5 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 ${className}`}
      >
        <Info className="h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          <strong className="font-semibold">Advisory only.</strong>{' '}
          Final eligibility decisions rest with the state or local election authority.
        </span>
      </div>
    );
  }

  return (
    <div
      role="note"
      aria-label="VERIS deference notice"
      className={`mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900 ${className}`}
    >
      <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-amber-700" aria-hidden />
      <div className="space-y-1">
        <p className="font-semibold">State and local election authority preserved.</p>
        <p className="text-amber-900/90">
          VERIS surfaces correlation, deduplication, and verification signals as advisories.
          It does not alter, supersede, or reinterpret constitutional authority over voter
          eligibility. All authoritative state changes require an explicit, human-confirmed
          action by an authorized official.
        </p>
      </div>
    </div>
  );
}
